/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ColumnFiltersState,
  ExpandedState,
  RowSelectionState,
  TableOptions,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { FC, Fragment, useEffect, useState } from "react";
import { Input, Spinner } from "src/components";
import { cn } from "src/utils/utils";
import arrowDownIcon from "../../assets/arrow-down.svg";
import { ClientPagination } from "./client-pagination.component";
import PaginationComponent from "./pagination.component";

interface TableProps {
  columns: any[];
  data: any[];
  onRowClick?: (row: any) => void;
  title?: string;
  subtitle?: string;
  isPaginating?: boolean;
  isGlobalSearch?: boolean;
  searchPlaceholder?: string;
  showheader?: boolean;
  renderSubComponent?: (props: any) => React.ReactElement;
  headClass?: string;
  getRowCanExpand?: (row: any) => boolean;
  loading?: boolean;
  onPaginationChange?: any;
  pagination?: any;
  manualPagination?: boolean;
  pageCount?: number;
  onGlobalSearch?: (value: string) => void;
  getSelectedRows?: any;
  isLoading?: boolean;
  showSearch?: boolean;
}

const Table1: FC<TableProps> = ({
  columns,
  data,
  onRowClick,
  title,
  subtitle,
  isPaginating = true,
  isGlobalSearch = true,
  searchPlaceholder,
  showheader = true,
  renderSubComponent,
  headClass,
  getRowCanExpand = () => false,
  isLoading = false,
  onPaginationChange = () => {},
  pageCount,
  pagination,
  manualPagination = false,
  onGlobalSearch,
  getSelectedRows,
  showSearch = true,
}) => {
  const [columnFilters, setColumnsFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const reactTableProps: TableOptions<any> = {
    data,
    columns,
    enableRowSelection: true,
    manualPagination: manualPagination,
    state: manualPagination
      ? {
          pagination,
          columnFilters,
          rowSelection,
          expanded,
          globalFilter,
        }
      : {
          columnFilters,
          rowSelection,
          expanded,
          globalFilter,
        },
    autoResetExpanded: true,
    getRowCanExpand,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: isPaginating ? getPaginationRowModel() : undefined,
    onColumnFiltersChange: setColumnsFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
  };

  useEffect(() => {
    setExpanded({});
  }, [data]);

  if (manualPagination) {
    (reactTableProps["onPaginationChange"] = onPaginationChange), (reactTableProps["pageCount"] = pageCount);
  }

  const table = useReactTable(reactTableProps);

  const filterHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    table.getColumn("item_code")?.setFilterValue(e.target.value);
  };

  const globalFilterHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onGlobalSearch) setGlobalFilter(String(e.target.value));
    else onGlobalSearch(String(e.target.value));
  };

  useEffect(() => {
    if (getSelectedRows) getSelectedRows(table.getSelectedRowModel().rows);
  }, [table.getSelectedRowModel()]);


  return (
    <>
      {title && (
        <section className="flex items-center justify-between mt-5 mb-6">
          <div>
            <h4 className="text-2xl font-semibold text-[#49484C]">{title}</h4>
            <p className="text-base font-medium text-[#49484C] opacity-60">{subtitle}</p>
          </div>
          {showSearch && (
            <div className="inline-flex items-center gap-2">
              <Input
                type="input"
                value={globalFilter}
                placeholder={searchPlaceholder || "Search..."}
                onChange={isGlobalSearch ? globalFilterHandler : filterHandler}
                className="px-3 rounded-lg text-base text-[#1B1A1F]"
                showIcon={true}
              />
            </div>
          )}
        </section>
      )}

      {isLoading && (
        <div className="flex justify-center w-full py-5">
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <>
          <div className="relative z-0 bg-white min-w-[600px] overflow-auto" style={{ height: "100vh" }}>
            <table className="w-full text-sm text-left text-gray-500 border-separate table-auto border-spacing-0">
              {showheader && (
                <thead className={cn(`pt-4 w-full ${headClass ? headClass : ""}`)}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr className="border-b divide-y divide-gray-200" key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          style={{ width: header.column.getSize() || "auto", textAlign: "left" }} // Align text to left
                          className="py-3 px-4 text-sm font-normal overflow-hidden text-[#49484cd3]"
                          key={header.id}
                        >
                          <div className="flex items-center max-w-full gap-2 overflow-hidden">
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanSort() && (
                              <button onClick={header.column.getToggleSortingHandler()}>
                                <img src={arrowDownIcon} alt="sorting" />
                              </button>
                            )}
                            {{
                              asc: " 🔼",
                              desc: " 🔽",
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
              )}

              {data?.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={table.getAllColumns().length} className="text-center">
                      <p className="mt-10 mb-10 text-3xl">No data...</p>
                    </td>
                  </tr>
                </tbody>
              )}

              {data && data.length > 0 && (
                <tbody className="bg-white text-[#49484C]">
                  {table.getRowModel().rows.map((row, rowIndex) => {
                    const isFirstRow = rowIndex === 0;
                    const isLastRow = rowIndex === table.getRowModel().rows.length - 1;

                    const subComponentElement = renderSubComponent ? renderSubComponent(row) : null;


                    return (
                      <Fragment key={row.id}>
                        <tr
                          className={`${onRowClick ? "hover:bg-gray-200 cursor-pointer" : ""}`}
                          style={{
                            borderTop: "1px solid #ccc",
                            borderBottom: isLastRow ? "1px solid #ccc" : row.getIsExpanded() ? "none" : "1px solid #ccc",
                            borderLeft: "1px solid #ccc",
                            borderRight: "1px solid #ccc",
                            borderTopLeftRadius: isFirstRow ? "0.625rem" : "0",
                            borderTopRightRadius: isFirstRow ? "0.625rem" : "0",
                            borderBottomLeftRadius: isLastRow && !row.getIsExpanded() ? "0.625rem" : "0",
                            borderBottomRightRadius: isLastRow && !row.getIsExpanded() ? "0.625rem" : "0",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onRowClick) onRowClick(row);
                          }}
                        >
                          {row.getVisibleCells().map((cell, cellIndex) => (
                            <td
                              key={cell.id}
                              className="px-4 py-3 text-sm font-semibold text-ellipsis line-height-normal whitespace-nowrap"
                              style={{
                                borderTop: "1px solid #ccc",
                                borderBottom: row.getIsExpanded() ? "none" : "1px solid #ccc",
                                borderLeft: cellIndex === 0 ? "1px solid #ccc" : "none",
                                borderRight: cellIndex === row.getVisibleCells().length - 1 ? "1px solid #ccc" : "none",
                                borderTopLeftRadius: cellIndex === 0 ? "0.625rem" : "0",
                                borderTopRightRadius: cellIndex === row.getVisibleCells().length - 1 ? "0.625rem" : "0",
                                borderBottomLeftRadius: row.getIsExpanded() ? "0" : cellIndex === 0 ? "0.625rem" : "0",
                                borderBottomRightRadius: row.getIsExpanded()
                                  ? "0"
                                  : cellIndex === row.getVisibleCells().length - 1
                                    ? "0.625rem"
                                    : "0",
                                textAlign: "left", // Align text to left
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>

                        {row.getIsExpanded() && subComponentElement && (
                          <tr
                            style={{
                              borderBottom: "1px solid #ccc",
                            }}
                          >
                            <td colSpan={row.getVisibleCells().length} style={{ padding: 0, borderBottomLeftRadius: "0.625rem" }}>
                              {subComponentElement}
                            </td>
                          </tr>
                        )}
                        <br />
                      </Fragment>
                    );
                  })}
                </tbody>
              )}

              <tfoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{ textAlign: "left" }} // Align text to left
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
          {isPaginating && !manualPagination && <ClientPagination table={table} />}
          {isPaginating && manualPagination ? <PaginationComponent table={table} /> : null}
        </>
      )}
    </>
  );
};

export default Table1;
